/* eslint-disable */
// @ts-ignore
import jump from 'jump.js'
import Image from 'next/image'
import React, { ComponentProps, useCallback } from 'react'

import { pxToRem } from '@/shared/styles/themes'
import { faFacebook } from '@fortawesome/free-brands-svg-icons/faFacebook'
import { faInstagram } from '@fortawesome/free-brands-svg-icons/faInstagram'
import { faTiktok } from '@fortawesome/free-brands-svg-icons/faTiktok'
import { IconLookup } from '@fortawesome/fontawesome-common-types'
import { Box, Container, Grid, styled, Typography } from '@mui/material'

import { Icon } from '../Icon/Icon'
import { IconButton } from '../IconButton/IconButton'
import { PixieWorldLogo } from '../Logo/PixieWorldLogo'
import { StyledAngleIcon } from './StyledAccordion'

const APP_STORE_URL = 'https://apps.apple.com/au/app/pixie-world-photo-storybook/id6755897042'

export type FooterMainProps = Omit<ComponentProps<typeof Grid>, 'justifyContent' | 'alignItems'> & {
  //
}

// TODO: Change the href for these social icons
export const FooterMain: React.FC<FooterMainProps> = ({ children, ...props }) => {
  const scrollToTop = useCallback(() => {
    jump('#__next', {
      duration: 800,
      a11y: true,
    })
  }, [])

  return (
    <StyledWrapperBox>
      <Container maxWidth="lg">
        <StyledGrid container justifyContent="space-between" {...props}>
          <Grid item xs={12} sm={12} md={3} >
            <StyledBox>
              <StyledLogoWrapper>
                <StyledLogoLink
                  href="/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Pixie World"
                >
                  <PixieWorldLogo
                    colorVariant="color"
                    priority={false}
                    loading="lazy"
                  />
                </StyledLogoLink>
              </StyledLogoWrapper>
              <StyledMissionTypography variant="body1">
                PixieWorld creates personalised storybooks that bring your child&apos;s imagination to life. Every book is a unique adventure with your little one as the star.
              </StyledMissionTypography>
              <StyledCommunity variant="body1">
                Join the community
              </StyledCommunity>
              <StyledIconGrid container justifyContent="space-between" alignItems="center">
                <Grid item>
                  <StyledSocialLink
                    href="https://www.instagram.com/pixieworldgifts"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Follow us on Instagram"
                  >
                    <StyledSocialIcon icon={faInstagram as IconLookup} />
                  </StyledSocialLink>
                </Grid>
                <Grid item>
                  <StyledSocialLink
                    href="https://www.tiktok.com/@pixieworldstories"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Follow us on TikTok"
                  >
                    <StyledSocialIcon icon={faTiktok as IconLookup} />
                  </StyledSocialLink>
                </Grid>
                <Grid item>
                  <StyledSocialLink
                    href="https://www.facebook.com/profile.php?id=61583871484712"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Join us on Facebook"
                  >
                    <StyledSocialIcon icon={faFacebook as IconLookup} />
                  </StyledSocialLink>
                </Grid>
              </StyledIconGrid>
              <StyledAppLabel variant="body1">
                Get the app for iPhone & iPad
              </StyledAppLabel>
              <StyledAppStoreLink
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download Pixie World for iPhone and iPad on the App Store"
              >
                <Image
                  src="/images/AppStoreButton.svg"
                  alt="Download on the App Store"
                  width={156}
                  height={46}
                  loading="lazy"
                />
              </StyledAppStoreLink>
            </StyledBox>
          </Grid>
          <UpButton
            aria-label="Scroll to top"
            onClick={scrollToTop}
          >
            <StyledAngleIcon iconCode="anglesmallup" />
          </UpButton>
          {children}
        </StyledGrid>
      </Container>
    </StyledWrapperBox>

  )
}

const StyledLogoLink = styled('a')`
  display: inline-block;
  text-decoration: none;
  color: inherit;
`

const StyledSocialLink = styled('a')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: inherit;
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: ${props => props.theme.palette.primary.main};
  }

  ${props => props.theme.breakpoints.down('md')} {
    background-color: ${props => props.theme.palette.primary.main};
    color: ${props => props.theme.palette.background.default};
    padding: ${pxToRem(12)};
    border-radius: ${pxToRem(12)};
  }

  ${props => props.theme.breakpoints.up('md')} {
    color: ${props => props.theme.palette.custom.footer.text};
  }
`

const UpButton = styled(IconButton)`
  position: absolute;
  background-color: ${(props) => props.theme.palette.text.primary};
  width: ${(props) => props.theme.typography.pxToRem(54)} !important;
  height: ${(props) => props.theme.typography.pxToRem(54)} !important;
  border-radius: 100%;
  top: -${(props) => props.theme.typography.pxToRem(27)};
  right: calc(50% - ${(props) => props.theme.typography.pxToRem(27)});

  &:hover {
    background-color: ${(props) => props.theme.palette.secondary.main};
  }

  ${(props) => props.theme.breakpoints.up('sm')} {
    right: 0;
  }
`

const StyledWrapperBox = styled(Box)`
  width: 100%;
  background-color: ${(props) => props.theme.palette.primary.main};
`

const StyledGrid = styled(Grid)`
  position: relative;
  padding-top: ${(props) => props.theme.typography.pxToRem(56)};
  padding-bottom: ${(props) => props.theme.typography.pxToRem(42)};

  ${(props) => props.theme.breakpoints.up('md')} {
    padding-top: ${(props) => props.theme.typography.pxToRem(42)};
  }
`

const StyledLogoWrapper = styled(Box)`
  margin-bottom: ${(props) => props.theme.typography.pxToRem(20)};
`

const StyledBox = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: ${(props) => props.theme.typography.pxToRem(40)};

  ${(props) => props.theme.breakpoints.up('md')} {
    align-items: flex-start;
    margin-bottom: 0;
  }
`

const StyledMissionTypography = styled(Typography)`
  text-align: center;
  margin-bottom: ${(props) => props.theme.typography.pxToRem(24)};
  color: ${(props) => props.theme.palette.custom.footer.text};

  ${(props) => props.theme.breakpoints.up('sm')} {
    max-width: ${(props) => props.theme.typography.pxToRem(380)};
  }

  ${(props) => props.theme.breakpoints.up('md')} {
    text-align: left;
  }
`

const StyledCommunity = styled(Typography)`
  font-weight: 500;
  margin-bottom: ${(props) => props.theme.typography.pxToRem(15)};
  color: ${(props) => props.theme.palette.custom.footer.text};
`

const StyledIconGrid = styled(Grid)`
  max-width: ${(props) => props.theme.typography.pxToRem(220)};

  ${(props) => props.theme.breakpoints.up('md')} {
    max-width: ${(props) => props.theme.typography.pxToRem(168)};
  }
`

const StyledAppLabel = styled(Typography)`
  font-weight: 500;
  margin-top: ${(props) => props.theme.typography.pxToRem(24)};
  margin-bottom: ${(props) => props.theme.typography.pxToRem(12)};
  color: ${(props) => props.theme.palette.custom.footer.text};
`

const StyledAppStoreLink = styled('a')`
  display: inline-block;
  line-height: 0;
  transition: opacity 0.2s ease, transform 0.2s ease;

  &:hover {
    opacity: 0.85;
    transform: translateY(-1px);
  }
`

export const SyledLink = styled('a')`
`

const StyledSocialIcon = styled(Icon)`
  color: ${(props) => props.theme.palette.background.default};
  width: ${(props) => props.theme.typography.pxToRem(24)};
  height: ${(props) => props.theme.typography.pxToRem(24)};

  ${(props) => props.theme.breakpoints.up('md')} {
    color: ${(props) => props.theme.palette.custom.footer.text};
  }
`
